'use client';

import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/**
 * SmoothShrink — asymmetric height animator.
 *
 * - Grow: snaps to the new height instantly (no transition), so freshly arrived
 *   content appears without lag.
 * - Shrink: animates `height` over ~480ms with a soft cubic-bezier so the
 *   container settles smoothly when inner content (e.g. an auto-closing
 *   tool-call details panel) disappears.
 * - Honors `prefers-reduced-motion`: shrink is also instant in that case.
 */
export function SmoothShrink({
  children,
  durationMs = 480,
}: {
  children: ReactNode;
  durationMs?: number;
}) {
  const innerRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<number>(0);
  const reducedMotionRef = useRef<boolean>(false);
  const [height, setHeight] = useState<number | 'auto'>('auto');
  const [shrinking, setShrinking] = useState(false);

  useLayoutEffect(() => {
    if (typeof window !== 'undefined' && 'matchMedia' in window) {
      reducedMotionRef.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    const el = innerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      const next = Math.round(entry.contentRect.height);
      const prev = prevRef.current;
      if (next === prev) return;
      if (next >= prev || reducedMotionRef.current) {
        setShrinking(false);
      } else {
        setShrinking(true);
      }
      prevRef.current = next;
      setHeight(next);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      style={{
        height: height === 'auto' ? undefined : height,
        transition: shrinking
          ? `height ${durationMs}ms cubic-bezier(0.16, 1, 0.3, 1)`
          : 'none',
        overflow: 'hidden',
        willChange: 'height',
      }}
    >
      <div ref={innerRef}>{children}</div>
    </div>
  );
}
